import { useState, useMemo, memo, SyntheticEvent, useEffect, useCallback } from "react";
import { Dimmer, Header, Loader, Tab, TabPane, TabProps } from "semantic-ui-react";

// Components
import CardMeCastMemberGroup from "./CardMeCastMemberGroup";
import CardMeCastList from "./CardMeCastList";
import CardBroadcastEditor from "./CardBroadcastEditor";
import ModBuyBillingPackage from "../MEH/ModBuyBillingPackage";
import ModInReview from "./ModInReview";
import ModHandleMemberNode from "./ModHandleMemberNode";

// Commons
import SnackMessage from "../common/SnackMessage";

// Types
import {
  RunSequence as MeCastRunSequence,
  SetProp,
  State,
  PickedState,
  BroadcastPostParams,
  BroadcastMode,
  ActionPostBroadcast,
  MemberGroupListParams,
  MemberGroupNodeParams,
} from "./MeCastType";
import { BROADCAST_CARD_NAME, RunSequence as MeFormRunSequence } from "./MeFormType";

// Interface
import { createMeCastBroadcastAdapter } from "./BroadcastAdapter";

// Styles
import "./styles/CardMeCastList.scss";

// Serializers
import BroadcastPostSerializer from "issara-sdk/types/BroadcastPostSerializer_apps_PRX";
import { useBroadcastListEditor, useBroadcastListFilter, useMemberGroupListFilter, useMemberNodeEditor } from "./MeCastHook";

// Type
type CardMeCastMainProps = {
  runSequence: MeCastRunSequence & MeFormRunSequence;
  setProp: SetProp;
  MeCastSequence: State["MeCastSequence"];
  selectedDivisionId: number;

  // Buy Credit
  BillingAndPackageSequence?: any;
  BillingCarts?: any;
  onHistoryClick?: () => void;
  controller?: any;
  onEvent?: any;
  languageUX?: "en" | "th";
  onProceed?: () => void;
  termsAndConditionsList?: any;

  // config
  defaultTabIndex?: number;
} & Pick<
  PickedState,
  | "buttonLoadCheck"
  | "errorMessage" 
  | "successMessage" 
  | "loadingStatus" 
  | "BillingCreditWallet" 
  | "userProfile" 
  | "selectedDivision"
>;

type PaneProps = {
  menuItem: { key: string; content: JSX.Element };
  render: () => JSX.Element;
};

const CardMeCastMain = (props: CardMeCastMainProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(props.defaultTabIndex ?? 0);
  const [openBuyCredit, setOpenBuyCredit] = useState<boolean>(false);

  const broadcastListFilter = useBroadcastListFilter({
    selectedDivisionId: props.selectedDivisionId,
  });

  const memberGroupListFilter = useMemberGroupListFilter();
  const memberNodeEditor = useMemberNodeEditor();

  const fetchMemberGroupList = (params?: {
    override?: Partial<MemberGroupListParams>;
    page?: number;
  }) => {
    const page = params?.page ?? memberGroupListFilter.activePage;

    props.runSequence({
      sequence: "MeCast",
      action: "GET_MEMBER_GROUP_LIST",
      activePage: page,
      filter: memberGroupListFilter.getResolvedParams(),
    });
  };

  const fetchMemberNodeList = (params?: {
    pdrNodeId?: number;
    filter?: MemberGroupNodeParams;
  }) => {
    const nodeId = params?.pdrNodeId ?? memberNodeEditor.pdrNodeId;

    if (!nodeId) return;

    props.runSequence({
      sequence: "MeCast",
      action: "GET_MEMBER_NODE_LIST",
      pdrNodeId: nodeId,
      filter: params?.filter ?? memberNodeEditor.getResolvedParams(),
    });
  };

  const fetchBroadcastList = (params?: {
    override?: Partial<BroadcastPostParams>;
    page?: number;
  }) => {
    const page = params?.page ?? broadcastListFilter.activePage;

    props.runSequence({
      sequence: "MeCast",
      action: "GET_BROADCAST_POST_LIST",
      activePage: page,
      broadcastParams: broadcastListFilter.getResolvedParams(),
    });
  };

  const broadcastListEditor = useBroadcastListEditor({
    onFetchList: fetchBroadcastList,
  });

  useEffect(() => {
    props.runSequence({
      sequence: "MeCast",
      restart: true,
      broadcastParams: broadcastListFilter.getResolvedParams(),
      memberGroupParams: memberGroupListFilter.getResolvedParams(),
    } as any);
  }, [props.selectedDivision]);

  useEffect(() => {
    if (props.defaultTabIndex !== undefined) {
      setActiveTabIndex(props.defaultTabIndex);
    }
  }, [props.defaultTabIndex]);

  useEffect(() => {
    if (props.MeCastSequence?.broadcastListTab?.broadcastDetail) {
      broadcastListEditor.updateSelectedBroadcast(
        props.MeCastSequence?.broadcastListTab?.broadcastDetail
      );
    }
  }, [props.MeCastSequence?.broadcastListTab]);

  useEffect(() => {
    broadcastListEditor.checkAndShowInReview(
      broadcastListEditor.selectedBroadcast,
      broadcastListEditor.mode,
    );
  }, [broadcastListEditor.selectedBroadcast, broadcastListEditor.mode]);

  useEffect(() => {
    if (memberNodeEditor.isOpen && memberNodeEditor.selectedNode) {
      props.runSequence({
        sequence: "MeCast",
        action: "GET_MEMBER_NODE_LIST",
        pdrNodeId: memberNodeEditor.selectedNode.node.node,
        filter: {},
      });
    }
  }, [memberNodeEditor.isOpen, memberNodeEditor.selectedNode]);

  const handleChangeTab = (e: SyntheticEvent, data: TabProps) => {
    setActiveTabIndex(data.activeIndex as number);
  };

  const handleCloseSnackMessage = () => {
    props.setProp(`errorMessage.${BROADCAST_CARD_NAME}`, null);
    props.setProp(`successMessage.${BROADCAST_CARD_NAME}`, null);
  };

  const handleBroadcastListChangePage = (page: number) => {
    broadcastListFilter.handleChangePage(page);
    fetchBroadcastList({ page });
  };

  const handleMemberGroupListChangePage = (page: number) => {
    memberGroupListFilter.handleChangePage(page);
    fetchMemberGroupList({ page });
  };

  const handleBroadcastListRefresh = () => {
    broadcastListFilter.handleRefresh();
    fetchBroadcastList();
  };

  const handleMemberGroupListRefresh = () => {
    memberGroupListFilter.handleRefresh();
    fetchMemberGroupList();
  };

  const handleMemberNodeListRefresh = () => {
    memberNodeEditor.handleRefresh();
    fetchMemberNodeList();
  };

  const handleSaveMemberNode = () => {
    if (!memberNodeEditor.pdrNodeId) return;

    const saveData = memberNodeEditor.getSaveData();

    if (saveData.length === 0) {
      console.warn("No Changes to save");
      return;
    }

    props.runSequence({
      sequence: "MeCast",
      action: "ACTION_MEMBER_NODE",
      data: {
        items: saveData,
      },
      pdrNodeId: memberNodeEditor.pdrNodeId,
      filter: memberNodeEditor.getResolvedParams(),
    });
  };

  const handleSearchMemberNode = () => {
    fetchMemberNodeList();
  };

  const handleOpenCreateBroadcast = (mode: BroadcastMode, row?: BroadcastPostSerializer) => {
    if (mode === "CREATE") {
      props.runSequence({
        sequence: "MeCast",
        action: "ADD_CONTENT",
      });
    }

    if (mode === "EDIT" && row) {
      props.runSequence({
        sequence: "MeCast",
        action: "GET_CONTENT",
        selectedBroadcast: row,
      });
      broadcastListEditor.selectBroadcast(row, "EDIT");
      return;
    }

    broadcastListEditor.openEditor(mode);
  };

  const handleSelectBroadcast = (selected: BroadcastPostSerializer) => {
    if (!selected) return;

    props.runSequence({
      sequence: "MeCast",
      action: "GET_CONTENT",
      selectedBroadcast: selected,
    });

    broadcastListEditor.selectBroadcast(selected, "VIEW");
  };

  const handleEditBroadcast = (selected: BroadcastPostSerializer) => {
    if (!selected) return;

    props.runSequence({
      sequence: "MeCast",
      action: "GET_CONTENT",
      selectedBroadcast: selected,
    });
  
    broadcastListEditor.selectBroadcast(selected, "EDIT");
  };

  const handleDuplicateBroadcast = useCallback(
    (
      selected: BroadcastPostSerializer,
      broadcastParams?: BroadcastPostParams,
      onSuccess?: (duplicatedRow: BroadcastPostSerializer) => void
    ) => {
      if (!selected.id) return;

      props.runSequence({
        sequence: "MeCast",
        action: "DUPLICATE_BROADCAST",
        broadcastPostId: selected.id,
        broadcastParams,
        onSuccess,
      });
    },
    [props.runSequence]
  );

  const handleBroadcastAction = useCallback((
    row: BroadcastPostSerializer,
    action: ActionPostBroadcast,
    broadcastParams?: BroadcastPostParams
  ) => {
    if (!row.id) return;

    props.runSequence({
      sequence: "MeCast",
      action: "ACTION_BROADCAST",
      broadcastPostId: row.id,
      actionParams: { action },
      broadcastParams: broadcastParams ?? broadcastListFilter.getResolvedParams(),
    });
  }, [props.runSequence, broadcastListFilter]);

  const handleOpenBuyCredit = () => setOpenBuyCredit(true);
  const handleCloseBuyCredit = () => setOpenBuyCredit(false);

  const panes: PaneProps[] = useMemo(
    () => [
      {
        menuItem: {
          key: "broadcast_list",
          content: (
            <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
              <label>Broadcast List</label>
            </div>
          ),
        },
        render: () => (
          <TabPane>
            <CardMeCastList
              // data
              broadcastListTab={props.MeCastSequence?.broadcastListTab}
              // actions
              onOpenCreateBroadcast={handleOpenCreateBroadcast}
              onSelectBroadcast={handleSelectBroadcast}
              onEditBroadcast={handleEditBroadcast}
              onDuplicateBroadcast={handleDuplicateBroadcast}
              onBroadcastAction={handleBroadcastAction}
              // search
              filter={broadcastListFilter.filter}
              latestRefresh={broadcastListFilter.latestRefresh}
              broadcastParams={broadcastListFilter.getResolvedParams()}
              onChangeData={broadcastListFilter.handleChangeData}
              onChangeDate={broadcastListFilter.handleChangeDate}
              onToggleStatus={broadcastListFilter.handleToggleStatus}
              onRefresh={handleBroadcastListRefresh}
              onSearch={() => fetchBroadcastList()}
              // pagination
              activePage={broadcastListFilter.activePage}
              onChangePage={handleBroadcastListChangePage}
              // common infra
              loadingStatus={props.loadingStatus}
            />
          </TabPane>
        ),
      },
      {
        menuItem: {
          key: "member_group",
          content: (
            <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
              <label>Member Group</label>
            </div>
          ),
        },
        render: () => (
          <TabPane>
            <CardMeCastMemberGroup
              // data 
              memberGroupTab={props.MeCastSequence?.memberGroupTab}
              // options
              diagFormList={props.MeCastSequence?.commonData?.diagFormList}
              pdrNodeList={props.MeCastSequence?.commonData?.pdrNodeList}
              // actions
              onOpenMemberNode={memberNodeEditor.openModal}
              // search
              filter={memberGroupListFilter.filter}
              latestRefresh={memberGroupListFilter.latestRefresh}
              memberGroupListParams={memberGroupListFilter.getResolvedParams()}
              onChangeData={memberGroupListFilter.handleChangeData}
              onChangeDate={memberGroupListFilter.handleChangeDate}
              onRefresh={handleMemberGroupListRefresh}
              onSearch={() => fetchMemberGroupList()}
              // pagination
              activePage={memberGroupListFilter.activePage}
              onChangePage={handleMemberGroupListChangePage}
              // infra
              setProp={props.setProp}
              // common infra
              buttonLoadCheck={props.buttonLoadCheck}
            />
          </TabPane>
        ),
      },
      // todo --- Insights Tab
      // {
      //   menuItem: {
      //     key: "insights",
      //     content: (
      //       <div style={{ alignItems: "center", width: "10rem", justifyContent: "center" }}>
      //         <label>Insights</label>
      //       </div>
      //     ),
      //   },
      //   render: () => (
      //     <TabPane>
      //       <div>
      //         {/* Insights Page */}
      //       </div>
      //     </TabPane>
      //   ),
      // },
    ],
    [
      props.MeCastSequence,
      props.loadingStatus,
      broadcastListEditor.header,
      broadcastListFilter,
      memberGroupListFilter,
      memberNodeEditor,
    ]
  );

  // console.log("Yeti [MeCastMain] props: ", props);

  return (
    <>
      <div style={{ padding: "15px 0", height: "100%" }}>
        {/* --- Loader --- */}
        <Dimmer active={props.loadingStatus?.[BROADCAST_CARD_NAME]} inverted>
          <Loader inverted>Loading</Loader>
        </Dimmer>

        {/* --- ErrorMessage & SuccessMessage --- */}
        <SnackMessage
          error={props.errorMessage?.[BROADCAST_CARD_NAME]}
          success={props.successMessage?.[BROADCAST_CARD_NAME]}
          onClose={handleCloseSnackMessage}
        />

        {/* --- In Review Mod --- */}
        <ModInReview
          open={broadcastListEditor.isInReviewOpen}
          onClose={broadcastListEditor.closeInReviewPopup}
          onBack={broadcastListEditor.closeEditor}
          // data
          broadcast={broadcastListEditor.selectedBroadcast}
          updatedByUserFirebase={props.MeCastSequence?.broadcastListTab?.updatedByUserFirebase}
        />

        {/* --- Mod Buy Credit --- */}
        <ModBuyBillingPackage
          // func & callback
          open={openBuyCredit}
          onClose={handleCloseBuyCredit}
          // CardBuyBillingPackageProps
          BillingAndPackageSequence={props.BillingAndPackageSequence}
          BillingCreditWallet={props.BillingCreditWallet}
          BillingCarts={props.BillingCarts}
          onHistoryClick={props.onHistoryClick}
          controller={props.controller}
          runSequence={props.runSequence}
          onEvent={props.onEvent}
          setProp={props.setProp}
          onProceed={props.onProceed}
          termsAndConditionsList={props.termsAndConditionsList}
          languageUX={props.languageUX as any}
        />

        {/* --- Mod Handle Member Node --- */}
        <ModHandleMemberNode
          // func & callback
          open={memberNodeEditor.isOpen}
          onClose={memberNodeEditor.closeModal}
          // data
          selectedNode={memberNodeEditor.selectedNode}
          memberNodeList={props.MeCastSequence?.memberGroupTab?.memberNodeList || []}
          patientDetailMap={props.MeCastSequence?.memberGroupTab?.patientDetailMap || {}}
          changedMembers={memberNodeEditor.changedMembers}
          hasChanges={memberNodeEditor.hasChanges}
          // filter
          filter={memberNodeEditor.filter}
          latestRefresh={memberNodeEditor.latestRefresh}
          onChangeData={memberNodeEditor.handleChangeData}
          onToggleShowInactive={memberNodeEditor.handleToggleShowInactive}
          onSearch={handleSearchMemberNode}
          onRefresh={handleMemberNodeListRefresh}
          onSave={handleSaveMemberNode}
          // member change
          onMemberActiveChange={memberNodeEditor.handleMemberActiveChange}
          // infra
          setProp={props.setProp}
          buttonLoadCheck={props.buttonLoadCheck}
        />

        {broadcastListEditor.isOpen? (
          <CardBroadcastEditor
            sequenceName="MeCast"

            mode={broadcastListEditor.mode}
            header={broadcastListEditor.header}
            context={{
              broadcast: broadcastListEditor.selectedBroadcast,
              firebase: props.MeCastSequence?.firebaseData?.selectedBroadcast,
              
              // Form
              pdrNodeList: props.MeCastSequence?.commonData?.pdrNodeList || [],
              patientByLeafList: props.MeCastSequence?.broadcastListTab?.patientByLeafList,
              
              // Infra
              postCalculateCredit: props.MeCastSequence?.broadcastListTab?.postCalculateCredit,
              updatedByUserFirebase: props.MeCastSequence?.broadcastListTab?.updatedByUserFirebase,
            }}
            actions={
              createMeCastBroadcastAdapter({
                mode: broadcastListEditor.mode,
                runSequence: props.runSequence,
                selectedBroadcast: broadcastListEditor.selectedBroadcast,
                selectedFirebase: props.MeCastSequence?.firebaseData?.selectedBroadcast
              })
            }
            onBack={broadcastListEditor.closeEditor}
            BillingCreditWallet={props.BillingCreditWallet}
            buttonLoadCheck={props.buttonLoadCheck}
            onOpenBuyCredit={handleOpenBuyCredit}
          />
        ) : (
          <div className="card-mecast-list">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "20px",
                padding: "0 15px",
              }}
            >
              <Header style={{ color: "rgb(79, 79, 79)", margin: 0 }} as="h2">
                MeCast
              </Header>
            </div>

            <Tab
              activeIndex={activeTabIndex}
              menu={{ pointing: true, secondary: true }}
              panes={panes}
              onTabChange={handleChangeTab}
            />
          </div>
        )}
      </div>
    </>
  );
};

CardMeCastMain.displayName = "CardMeCastMain";

export default memo(CardMeCastMain);
